import React, { useRef, useState } from "react";
import { Field, useFormikContext } from "formik";
import moment from "moment";

import {
  Button,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import TabPanel from "../TabPanel";

/**
 * Tab "Mensagens" — saudação, conclusão, fora de expediente e férias coletivas.
 * Compartilhada entre WhatsAppModal (nativo) e PluginConnectionModal.
 * Lê dados do Formik via useFormikContext — deve renderizar dentro de <Formik>.
 */
const MessagesTab = ({ tab, classes, whatsAppId }) => {
  const { values, touched, errors, setFieldValue } = useFormikContext();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // #4420 — upload do anexo das férias coletivas. Só funciona com uma conexão
  // já salva (whatsAppId presente); numa criação, o botão fica desabilitado.
  const handleVacationFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !whatsAppId) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("typeArch", "collectiveVacation");
    setUploading(true);
    try {
      const { data } = await api.post(
        `/whatsapp/${whatsAppId}/vacation-media-upload`,
        formData
      );
      setFieldValue("collectiveVacationMediaPath", data?.mediaPath || file.name);
      setFieldValue("collectiveVacationMediaName", data?.mediaName || file.name);
      toast.success(i18n.t("whatsappModal.form.vacationMediaUploaded"));
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleVacationFileRemove = async () => {
    if (!whatsAppId) {
      setFieldValue("collectiveVacationMediaPath", null);
      setFieldValue("collectiveVacationMediaName", null);
      return;
    }
    setUploading(true);
    try {
      await api.delete(`/whatsapp/${whatsAppId}/vacation-media-upload`);
      setFieldValue("collectiveVacationMediaPath", null);
      setFieldValue("collectiveVacationMediaName", null);
      toast.success(i18n.t("whatsappModal.form.vacationMediaRemoved"));
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <TabPanel className={classes.container} value={tab} name="messages">
      <DialogContent dividers>
        <Grid container spacing={1}>
          {/* MENSAGEM DE SAUDAÇÃO */}
          <Grid item xs={12} md={12} xl={12}>
            <Field
              as={TextField}
              label={i18n.t("whatsappModal.form.greetingMessage")}
              type="greetingMessage"
              multiline
              rows={4}
              fullWidth
              name="greetingMessage"
              error={touched.greetingMessage && Boolean(errors.greetingMessage)}
              helperText={touched.greetingMessage && errors.greetingMessage}
              variant="outlined"
              margin="dense"
            />
          </Grid>

          {/* MENSAGEM DE CONCLUSÃO */}
          <Grid item xs={12} md={12} xl={12}>
            <Field
              as={TextField}
              label={i18n.t("whatsappModal.form.complationMessage")}
              multiline
              rows={4}
              fullWidth
              name="complationMessage"
              error={touched.complationMessage && Boolean(errors.complationMessage)}
              helperText={touched.complationMessage && errors.complationMessage}
              variant="outlined"
              margin="dense"
            />
          </Grid>

          {/* MENSAGEM DE FORA DE EXPEDIENTE */}
          <Grid item xs={12} md={12} xl={12}>
            <Field
              as={TextField}
              label={i18n.t("whatsappModal.form.outOfHoursMessage")}
              multiline
              rows={4}
              fullWidth
              name="outOfHoursMessage"
              error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
              helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
              variant="outlined"
              margin="dense"
            />
          </Grid>

          {/* MENSAGEM DE FÉRIAS COLETIVAS */}
          <Grid item xs={12} md={12} xl={12}>
            <Field
              as={TextField}
              label={i18n.t("whatsappModal.form.collectiveVacationMessage")}
              multiline
              rows={4}
              fullWidth
              name="collectiveVacationMessage"
              error={
                touched.collectiveVacationMessage &&
                Boolean(errors.collectiveVacationMessage)
              }
              helperText={
                touched.collectiveVacationMessage && errors.collectiveVacationMessage
              }
              variant="outlined"
              margin="dense"
            />
          </Grid>

          <Grid item xs={6}>
            {/* #4420 — datetime-local: data+hora. Antes era só data, e período
                de mesmo dia nunca batia (00:00 a 00:00 do mesmo dia). */}
            <Field
              fullWidth
              as={TextField}
              label={i18n.t("whatsappModal.form.collectiveVacationStart")}
              type="datetime-local"
              name="collectiveVacationStart"
              required={values.collectiveVacationMessage?.length > 0}
              inputProps={{
                min: moment().add(-10, "days").format("YYYY-MM-DDTHH:mm"),
              }}
              InputLabelProps={{ shrink: true }}
              error={
                touched.collectiveVacationStart &&
                Boolean(errors.collectiveVacationStart)
              }
              helperText={
                touched.collectiveVacationStart && errors.collectiveVacationStart
              }
              variant="outlined"
            />
          </Grid>

          <Grid item xs={6}>
            <Field
              fullWidth
              as={TextField}
              label={i18n.t("whatsappModal.form.collectiveVacationEnd")}
              type="datetime-local"
              name="collectiveVacationEnd"
              required={values.collectiveVacationMessage?.length > 0}
              inputProps={{
                min: moment().add(-10, "days").format("YYYY-MM-DDTHH:mm"),
              }}
              InputLabelProps={{ shrink: true }}
              error={
                touched.collectiveVacationEnd &&
                Boolean(errors.collectiveVacationEnd)
              }
              helperText={
                touched.collectiveVacationEnd && errors.collectiveVacationEnd
              }
              variant="outlined"
            />
          </Grid>

          {/* #4420 — repetição: quantas vezes o aviso de férias é enviado por
              conversa. 1 = só na primeira mensagem do cliente no período;
              valores maiores repetem até esse limite; 0/vazio = sem limite. */}
          <Grid item xs={6}>
            <Field
              fullWidth
              as={TextField}
              label={i18n.t("whatsappModal.form.maxUseCollectiveVacation")}
              type="number"
              name="maxUseCollectiveVacation"
              inputProps={{ min: 0, step: 1 }}
              InputLabelProps={{ shrink: true }}
              error={
                touched.maxUseCollectiveVacation &&
                Boolean(errors.maxUseCollectiveVacation)
              }
              helperText={
                (touched.maxUseCollectiveVacation &&
                  errors.maxUseCollectiveVacation) ||
                i18n.t("whatsappModal.form.maxUseCollectiveVacationHelp")
              }
              variant="outlined"
            />
          </Grid>

          {/* #4420 — anexo opcional (imagem/PDF) enviado junto da mensagem. */}
          <Grid item xs={6}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              onChange={handleVacationFile}
            />
            {values.collectiveVacationMediaName ? (
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs>
                  <Typography variant="body2" noWrap>
                    {values.collectiveVacationMediaName}
                  </Typography>
                </Grid>
                <Grid item>
                  <Tooltip
                    title={i18n.t("whatsappModal.form.removeVacationMedia")}
                  >
                    <IconButton
                      size="small"
                      onClick={handleVacationFileRemove}
                      disabled={uploading}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            ) : (
              <Tooltip
                title={
                  whatsAppId
                    ? i18n.t("whatsappModal.form.attachVacationMedia")
                    : i18n.t("whatsappModal.form.saveFirstToAttach")
                }
              >
                <span>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    disabled={uploading || !whatsAppId}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {i18n.t("whatsappModal.form.attachVacationMedia")}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </TabPanel>
  );
};

export default MessagesTab;
